import { Box } from "@primer/react";
import {
  HomeIcon,
  FileIcon,
  KeyIcon,
  PersonIcon,
  BookIcon,
  GraphIcon,
  CommentDiscussionIcon,
  PulseIcon,
} from "@primer/octicons-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import type { ComponentType } from "react";
import { FeedbackModal } from "./FeedbackModal";
import { Toast } from "./Toast";
import { AppIcon, LogoTextShape } from "../assets";

interface NavigationItem {
  label: string;
  path?: string;
  icon: ComponentType<{ size?: number }>;
  isActive?: (pathname: string) => boolean;
  onSelect?: () => void;
}

export function MainNavigation() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [showToast, setShowToast] = useState(false);

  const mainItems: NavigationItem[] = [
    {
      label: "Home",
      path: "/dashboard",
      icon: HomeIcon,
      isActive: (pathname) => pathname === "/dashboard",
    },
    {
      label: "Analytics",
      path: "/dashboard/analytics",
      icon: PulseIcon,
    },
    {
      label: "Templates",
      path: "/dashboard/templates",
      icon: FileIcon,
      isActive: (pathname) =>
        pathname.startsWith("/dashboard/templates") &&
        !pathname.includes("/visual"),
    },
    {
      label: "API Keys",
      path: "/dashboard/api-keys",
      icon: KeyIcon,
      isActive: (pathname) =>
        pathname === "/dashboard/api-keys" ||
        pathname.startsWith("/dashboard/api-usage/"),
    },
    {
      label: "Usage & Billing",
      path: "/dashboard/usage",
      icon: GraphIcon,
    },
  ];

  const utilityItems: NavigationItem[] = [
    {
      label: "Profile Settings",
      path: "/dashboard/settings",
      icon: PersonIcon,
    },
    {
      label: "API Documentation",
      path: "/api-documentation?section=generate-pdf",
      icon: BookIcon,
      isActive: (pathname) => pathname === "/api-documentation",
    },
    {
      label: "Feedback",
      icon: CommentDiscussionIcon,
      onSelect: () => setIsFeedbackModalOpen(true),
    },
  ];

  const renderItem = (item: NavigationItem) => {
    const Icon = item.icon;
    const active = item.isActive
      ? item.isActive(location.pathname)
      : item.path
        ? location.pathname === item.path.split("?")[0]
        : false;

    return (
      <button
        key={item.label}
        type="button"
        className={`app-nav-item${active ? " is-active" : ""}`}
        aria-label={item.label}
        title={item.label}
        aria-current={active ? "page" : undefined}
        onClick={() => {
          if (item.onSelect) item.onSelect();
          else if (item.path) navigate(item.path);
        }}
      >
        <Icon size={18} />
        <span className="app-nav-label">{item.label}</span>
      </button>
    );
  };

  return (
    <>
      <Box className="app-nav-shell">
        <button
          type="button"
          className="app-sidebar-logo"
          aria-label="Go to ForgePDF home"
          onClick={() => navigate("/dashboard")}
        >
          <img className="app-sidebar-wordmark" src={LogoTextShape} alt="ForgePDF" />
          <img className="app-sidebar-mark" src={AppIcon} alt="" />
        </button>

        <Box as="nav" aria-label="Primary navigation" className="app-nav-list">
          {mainItems.map(renderItem)}
        </Box>

        <Box className="app-nav-footer">
          <Box as="nav" aria-label="Support and account" className="app-nav-list">
            {utilityItems.map(renderItem)}
          </Box>
        </Box>
      </Box>

      <FeedbackModal
        isOpen={isFeedbackModalOpen}
        onDismiss={() => setIsFeedbackModalOpen(false)}
        onSuccess={() => {
          setShowToast(true);
          setTimeout(() => setShowToast(false), 3000);
        }}
      />

      {showToast && (
        <Toast message="Thank you for your feedback. We’ll review it carefully." />
      )}
    </>
  );
}
